// Feature: MCRO001-F0052 (Canvas track colour matches Video Library tile border)
// Test: T-MCRO-052
//
// ONE deterministic colour source shared by the Canvas demonstration timing rail
// (MCRO001-F0028) and the Video Library tile border (MCRO001-F0006), so a given
// video is the SAME colour in both places and a viewer can tell at a glance which
// rail belongs to which library tile.
//
// The colour is keyed by a video's STABLE track key — its `mediaId` — not by the
// per-map `demonstrationId`. A demonstration carries the `mediaId` of the video it
// times (MusicChoreography.demonstrationRecordToRawCandidate maps the server's
// `railColour` onto `candidate.colour` and copies `record.mediaId`), so keying by
// media is what makes the rail line and the library tile for the SAME video agree.
// One active timing map per `(revision, media)` is enforced upstream
// (MCRO001-F0043 / migration 111 `uq_choreo_demo_map_revision_media`), so distinct
// active demonstrations on a revision are distinct media — keying by media therefore
// preserves the F0028 acceptance that active rail lines are colour-distinct.
//
// Priority of colour, per candidate:
//   1. The server-assigned canonical `railColour` (candidate.colour), when present.
//      This is F0028 §4 / #920's collision-resolved per-map colour and stays the
//      canonical source — the library adopts it rather than inventing a second one.
//   2. Otherwise a deterministic FNV-1a hash of the track key -> HSL. This is the
//      SAME hash the rail used before this feature (demonstrationPriority.js), so a
//      video with no saved server colour still gets one stable colour both surfaces
//      derive identically, before any server round-trip.
//
// A track key falls back to `demonstrationId` when a candidate has no `mediaId`
// (older payloads / component-test fixtures), so the rail's existing per-line
// distinctness is never regressed.

/** The stable per-video key a colour is assigned against: mediaId, else demonstrationId. */
export function mediaTrackKey(candidate = {}) {
  const mediaId = candidate.mediaId != null ? String(candidate.mediaId).trim() : '';
  if (mediaId) return mediaId;
  const demonstrationId = candidate.demonstrationId != null ? String(candidate.demonstrationId).trim() : '';
  return demonstrationId;
}

/** Deterministic FNV-1a(key) -> `hsl(H 68% 39%)`. Identical to the rail's prior hash. */
export function deterministicTrackColour(key) {
  let hash = 2166136261;
  for (const char of String(key)) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  const hue = ((hash >>> 0) / 0x100000000) * 360;
  return `hsl(${hue.toFixed(3)} 68% 39%)`;
}

// A persisted server colour is trusted verbatim (trimmed). Falsy/blank means "none".
function persistedColour(candidate) {
  return typeof candidate?.colour === 'string' ? candidate.colour.trim() : '';
}

/**
 * Build the single colour map every surface reads: { [trackKey]: colour }, keyed by
 * mediaId (else demonstrationId). Prefers the persisted server `railColour`; falls
 * back to the deterministic hash of the same key. Membership-independent: adding,
 * removing or reordering candidates never recolours an existing video, because each
 * colour depends only on that video's own key/persisted value, never on its
 * neighbours or position. First persisted colour wins if two candidates share a key.
 */
export function mediaTrackColours(candidates = []) {
  return candidates.reduce((colours, candidate) => {
    const key = mediaTrackKey(candidate);
    if (!key) return colours;
    const persisted = persistedColour(candidate);
    if (persisted) {
      // A persisted colour is authoritative and wins over a previously-hashed value
      // for the same key; a later hash never overwrites it.
      colours[key] = persisted;
      return colours;
    }
    if (!(key in colours)) colours[key] = deterministicTrackColour(key);
    return colours;
  }, {});
}

/**
 * Look one video's colour up by mediaId out of a { [trackKey]: colour } map. Used by
 * the Video Library tile so its border matches the rail for the same video; returns
 * '' (no track colour) when the video has no rail line, so a video without a
 * demonstration keeps its ordinary selection border.
 */
export function trackColourForMedia(colours = {}, mediaId) {
  const key = mediaId != null ? String(mediaId).trim() : '';
  if (!key) return '';
  return colours[key] || '';
}
